import React from "react";
import { Tooltip } from "@chakra-ui/react";
import { calcWeightedScore, calcWeightedDiff, formatWeightedValue } from "../utils/scoring";
import { DiffBadge } from "./DiffBadge";
import type { SupplierScorePreview, CriterionScorePreview } from "../types/scoring.type";

interface ScorePreviewTableProps {
	criteria: CriterionScorePreview[];
	mode: "initial" | "final";
	suppliers: SupplierScorePreview[];
}

export const ScorePreviewTable = ({
	mode,
	suppliers,
	criteria,
}: ScorePreviewTableProps) => {
	if (criteria.length === 0 || suppliers.length === 0) {
		return null;
	}

	const isFinal = mode === "final";

	const supplierTotals = suppliers.map((supplier) => {
		let weightedTotal = 0;
		let initialWeightedTotal = 0;
		let hasAnyScore = false;
		let hasAnyInitial = false;

		criteria.forEach((c) => {
			const cellData = c.scores[supplier.inviteId];
			const w = calcWeightedScore(cellData?.score ?? null, c.criterionWeight);
			const iw = calcWeightedScore(cellData?.initialScore ?? null, c.criterionWeight);
			if (w !== null) { weightedTotal += w; hasAnyScore = true; }
			if (iw !== null) { initialWeightedTotal += iw; hasAnyInitial = true; }
		});

		return {
			inviteId: supplier.inviteId,
			weightedTotal: hasAnyScore ? weightedTotal : null,
			initialWeightedTotal: hasAnyInitial ? initialWeightedTotal : null,
		};
	});

	return (
		<div className="overflow-x-auto" data-testid="score-preview-table">
			<table className="w-full text-sm border-collapse">
				<thead>
					<tr className="border-b border-gray-200">
						<th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
							Criteria
						</th>
						<th className="text-center py-2 px-1 text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
							Wt%
						</th>
						{suppliers.map((supplier) => (
							<th
								key={supplier.inviteId}
								colSpan={2}
								className="text-center py-2 px-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								{supplier.supplierName}
							</th>
						))}
					</tr>
					<tr className="border-b border-gray-200">
						<th />
						<th />
						{suppliers.map((supplier) => (
							<React.Fragment key={supplier.inviteId}>
								<th className="text-center py-1 px-1 text-[10px] font-medium text-gray-400 uppercase">
									Score
								</th>
								<th className="text-center py-1 px-1 text-[10px] font-medium text-gray-400 uppercase">
									Wtd
								</th>
							</React.Fragment>
						))}
					</tr>
				</thead>
				<tbody>
					{criteria.map((criterion) => (
						<tr
							key={criterion.rfxCriteriaId}
							className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
							data-testid={`criterion-row-${criterion.rfxCriteriaId}`}
						>
							<td className="py-2 px-2 text-gray-700 font-medium">
								{criterion.criterionName}
							</td>
							<td className="text-center py-2 px-1 text-gray-500 text-xs">
								{criterion.criterionWeight}%
							</td>
							{suppliers.map((supplier) => {
								const cellData = criterion.scores[supplier.inviteId];
								const score = cellData?.score ?? null;
								const initialScore = cellData?.initialScore;
								const scoreDiff = isFinal ? calcWeightedDiff(score, initialScore, 100) : null;
								const weighted = calcWeightedScore(score, criterion.criterionWeight);
								const weightedDiff = isFinal ? calcWeightedDiff(score, initialScore, criterion.criterionWeight) : null;

								return (
									<React.Fragment key={supplier.inviteId}>
										<td
											className="text-center py-2 px-1"
											data-testid={`score-cell-${criterion.rfxCriteriaId}-${supplier.inviteId}`}
										>
											{score !== null ? (
												<Tooltip
													label={isFinal && initialScore != null ? `Initial: ${initialScore}` : undefined}
													placement="top"
													hasArrow
													isDisabled={!isFinal || initialScore == null}
												>
													<span className="font-medium">{score}</span>
												</Tooltip>
											) : (
												<span className="text-gray-400">-</span>
											)}
											{isFinal && (
												<DiffBadge diff={scoreDiff} />
											)}
										</td>
										<td
											className="text-center py-2 px-1"
											data-testid={`weighted-cell-${criterion.rfxCriteriaId}-${supplier.inviteId}`}
										>
											<span className="font-medium text-gray-600">
												{formatWeightedValue(weighted)}
											</span>
											{isFinal && (
												<DiffBadge diff={weightedDiff} />
											)}
										</td>
									</React.Fragment>
								);
							})}
						</tr>
					))}

					<tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
						<td className="py-2 px-2 text-gray-700">Total Weighted</td>
						<td />
						{supplierTotals.map((st) => {
							const diff = isFinal && st.weightedTotal !== null && st.initialWeightedTotal !== null
								? st.weightedTotal - st.initialWeightedTotal
								: null;
							return (
								<React.Fragment key={st.inviteId}>
									<td className="text-center py-2 px-1" />
									<td
										className="text-center py-2 px-1"
										data-testid={`weighted-total-${st.inviteId}`}
									>
										<span className="font-semibold text-gray-800">
											{formatWeightedValue(st.weightedTotal)}
										</span>
										{isFinal && (
											<DiffBadge diff={diff} />
										)}
									</td>
								</React.Fragment>
							);
						})}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

